import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import Modal from '../../common/Modal';
import FileUploader from '../../common/FileUploader';
import DropdownSelect from '../../common/DropdownSelect';
import { useFetch, useSubmission } from '../../../hooks/useCallApi';

export default function LegislationModal({
  isOpen,
  onClose,
  mode = 'create',
  legislation = null,
  latestNumber,
}) {
  const isEditMode = mode === 'edit' && Boolean(legislation);

  const [type, setType] = useState('resolution');
  const [docNumber, setDocNumber] = useState('');
  const [title, setTitle] = useState(legislation?.title || '');
  const [description, setDescription] = useState(
    legislation?.description || ''
  );
  const [dateFiled, setDateFiled] = useState(legislation?.date_filed || '');
  const [dateApproved, setDateApproved] = useState(
    legislation?.date_approved || ''
  );
  const [committee, setCommittee] = useState(legislation?.committee_id || null);
  const [chairmanship, setChairmanship] = useState(
    legislation?.chairmanship || null
  );
  const [author, setAuthor] = useState(legislation?.sponsor_ids || []);
  const [docFile, setDocFile] = useState(null);
  const [publicRecordFiles, setPublicRecordFiles] = useState([]);
  const [errors, setErrors] = useState({});

  console.log('publicRecordFiles', publicRecordFiles);
  const { data: sponsorsData } = useFetch(['sponsors-select'], (api) =>
    api.admin.legislation.getSponsorsSelect()
  );

  const { data: committeesData } = useFetch(['committees-select'], (api) =>
    api.admin.legislation.getCommitteesSelect()
  );

  const { data: chairmanshipsData } = useFetch(
    ['chairmanships-select'],
    (api) => api.admin.legislation.getChairmanshipSelect()
  );

  const { data: fetchedLegislation, isLoading: isFetchingLegislation } =
    useFetch(
      ['legislation', legislation?.legislative_document_id],
      (api) =>
        api.admin.legislation.getLegislation(
          legislation.legislative_document_id,
          {
            include: 'committee,sponsors,consultationRecords',
          }
        ),
      {
        enabled: !!legislation?.legislative_document_id && isOpen && isEditMode,
      }
    );

  const generateDocNumber = (lastNumber) => {
    const year = new Date().getFullYear();
    if (!lastNumber) return `${year}-0001`;

    const [lastYear, lastCount] = lastNumber.split('-');
    if (Number(lastYear) !== year) return `${year}-0001`;

    const nextCount = String(Number(lastCount) + 1).padStart(4, '0');
    return `${year}-${nextCount}`;
  };

  useEffect(() => {
    if (!isOpen || isEditMode || !latestNumber) return;
    setDocNumber((prev) => prev || generateDocNumber(latestNumber));
  }, [isOpen, isEditMode, latestNumber]);

  const { mutateAsync: createLegislationMutation } = useSubmission(
    ['create-legislation'],
    (api, args) => api.admin.legislation.create(args)
  );

  const { mutateAsync: updateLegislationMutation } = useSubmission(
    ['update-legislation'],
    (api, args) => api.admin.legislation.update(args)
  );

  useEffect(() => {
    if (isEditMode && fetchedLegislation?.data) {
      const data = fetchedLegislation.data;

      const docType = data?.doc_type?.toLowerCase();
      setType(
        docType === 'resolution' || docType === 'ordinance'
          ? docType
          : 'resolution'
      );

      setDocNumber(data.legislationNumber || data.doc_number);
      setTitle(data.title || '');
      setDescription(data.description || '');
      setDateFiled(data.date_filed || '');
      setDateApproved(data.date_approved || '');
      setCommittee(data.committee?.committee_id || null);
      const chair = data?.committee?.chairman_name || '';
      const chairMatch = chairmanshipsData?.data?.find(
        (c) => c.label.toLowerCase() === chair.toLowerCase()
      );
      setChairmanship(chairMatch?.value || '');
      setAuthor(data.sponsors?.map((s) => s.user_id) || []);
      setDocFile(data.legislative_file || null);
      setPublicRecordFiles(data.consultation_records || []);
    }
  }, [fetchedLegislation, isEditMode]);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (!committee) newErrors.committee = 'Committee is required';
    if (!author.length) newErrors.author = 'At least one author is required';

    setErrors(newErrors);
    if (Object.keys(newErrors).length)
      return toast.error('Please fill in all required fields.');

    const payload = {
      doc_type: type,
      doc_number: docNumber,
      title,
      description,
      date_filed: dateFiled,
      date_approved: dateApproved,
      committee_id: committee,
      chairmanship,
      sponsor_ids: author,
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(`${key}[]`, v));
      } else {
        formData.append(key, value);
      }
    });

    if (docFile?.type === 'new') {
      formData.append('legislative_file', docFile.file);
    }

    if (docFile?.type === 'existing' && docFile?.remove === 1) {
      formData.append('remove_legislative_file', 1);
    }

    publicRecordFiles.forEach((fileObj, index) => {
      if (fileObj.type === 'new') {
        formData.append(
          `consultation_records[${index}][public_files]`,
          fileObj.file
        );
      }

      if (fileObj.type === 'existing' && fileObj.remove === 1) {
        formData.append(
          `consultation_records[${index}][remove_public_files]`,
          1
        );
        formData.append(`consultation_records[${index}][id]`, fileObj.id);
      }
    });

    try {
      if (isEditMode) {
        const targetId = fetchedLegislation?.data?.id || legislation?.id;
        if (!targetId) return toast.error('Missing legislation ID.');
        await updateLegislationMutation({ id: targetId, payload: formData });
      } else {
        await createLegislationMutation(formData);
      }
      toast.success(
        `Legislation ${isEditMode ? 'updated' : 'created'} successfully.`
      );
      onClose();
    } catch (err) {
      toast.error('Failed to save legislation.');
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <div className='bg-gradient-to-r from-[#60A4F9] to-[#2865EC] px-3 py-1 rounded'>
              <h3 className='text-base text-white font-semibold'>
                {type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()} No.{' '}
                {docNumber}
              </h3>
            </div>
            <span className='text-md font-semibold text-gray-800'>
              Legislation Details
            </span>
          </div>
        </div>
      }
      footer={
        <div className='flex justify-end gap-3'>
          <button
            onClick={onClose}
            className='px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 transition'
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className='px-5 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white shadow-md hover:bg-blue-700 transition'
          >
            {isEditMode ? 'Update' : 'Create'}
          </button>
        </div>
      }
      size='6xl'
    >
      <div className='px-5 py-5 space-y-6 max-h-[70vh] overflow-y-auto'>
        {isEditMode && isFetchingLegislation && !fetchedLegislation && (
          <div className='text-center text-sm text-gray-500 py-10'>
            Loading legislation...
          </div>
        )}

        <div className='flex flex-col sm:flex-row sm:items-end gap-5'>
          <div className='flex flex-col gap-2 sm:flex-1'>
            <label className='block text-sm font-medium text-gray-700'>
              Legislation Type
            </label>
            <div className='flex gap-4 mt-1'>
              {['Resolution', 'Ordinance'].map((option) => (
                <label
                  key={option}
                  className={`flex items-center gap-2 cursor-pointer px-6 py-3 border rounded-lg flex-1 transition ${
                    type === option.toLowerCase()
                      ? 'border-blue-600 ring-2 ring-blue-100 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <input
                    type='radio'
                    name='type'
                    value={option.toLowerCase()} // lowercase value
                    checked={type === option.toLowerCase()} // compare lowercase
                    onChange={() => setType(option.toLowerCase())} // set lowercase
                    className='text-blue-600 focus:ring-blue-500'
                  />
                  {option} {/* Display with capital letter */}
                </label>
              ))}
            </div>
          </div>

          <div className='flex flex-col gap-2 sm:w-1/3'>
            <label className='block text-sm font-medium text-gray-700'>
              Legislation Number
            </label>
            <input
              type='text'
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder='Enter Legislation Number'
              className='w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>
        </div>

        <div className='flex flex-col sm:flex-row gap-5'>
          {[
            {
              label: 'Title',
              value: title,
              setter: setTitle,
              error: errors.title,
            },
            {
              label: 'Description',
              value: description,
              setter: setDescription,
              error: errors.description,
            },
          ].map(({ label, value, setter, error }) => (
            <div className='flex-1' key={label}>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {label}
              </label>
              <input
                type='text'
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder={`Enter ${label.toLowerCase()}`}
                className='w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
              {error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
            </div>
          ))}
        </div>

        <div className='flex flex-col sm:flex-row gap-5'>
          {[
            { label: 'Date Filed', value: dateFiled, setter: setDateFiled },
            {
              label: 'Date Approved',
              value: dateApproved,
              setter: setDateApproved,
            },
          ].map(({ label, value, setter }) => (
            <div className='flex-1' key={label}>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {label}
              </label>
              <input
                type='date'
                value={value}
                onChange={(e) => setter(e.target.value)}
                className='w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
          ))}
        </div>

        <div className='flex flex-col sm:flex-row gap-5 pb-2'>
          <DropdownSelect
            label='Committee'
            options={committeesData?.data || []}
            selected={committee}
            onChange={setCommittee}
            error={errors.committee}
          />
          <DropdownSelect
            label='Chairmanship'
            options={chairmanshipsData?.data || []}
            selected={chairmanship}
            onChange={setChairmanship}
            disabled={isEditMode}
          />
          <DropdownSelect
            label='Author / Main Sponsor'
            options={sponsorsData?.data || []}
            selected={author}
            onChange={setAuthor}
            multiple
            error={errors.author}
          />
        </div>

        <div className='flex flex-col md:flex-row gap-5'>
          <div className='bg-gray-50 p-5 rounded-xl w-full h-fit'>
            <FileUploader
              label='Legislation Document File'
              inputId='legislation-doc-upload'
              multiple={false}
              mode={isEditMode ? 'edit' : 'create'}
              initialFiles={docFile ? [docFile] : []}
              onFilesChange={(files) => setDocFile(files[0] || null)}
            />
          </div>

          <div className='bg-gray-50 p-5 rounded-xl w-full h-fit'>
            <FileUploader
              label='Public Records'
              inputId='public-records-upload'
              multiple
              mode={isEditMode ? 'edit' : 'create'}
              initialFiles={publicRecordFiles}
              onFilesChange={setPublicRecordFiles}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
